import { useEffect, useMemo, useState } from "react";
import { Activity, AlertCircle, Check, Loader2, Plus, Search, X } from "lucide-react";
import { vitalMeasurementService } from "../services/vitalMeasurementService";

const asBool = (value) => value === true || value === 1 || value === "1" || value === "true";
const parseValue = (value) => {
  if (typeof value !== "string") return value;
  const input = value.trim();
  if (!input) return "";
  try { return JSON.parse(input); } catch { return value; }
};
const normalize = (row = {}) => ({
  ...row,
  id: Number(row.id ?? row.measurementId ?? row.measurement_id),
  measurementKey: row.measurementKey ?? row.measurement_key ?? "",
  displayName: row.displayName ?? row.display_label ?? row.measurement_name ?? row.name ?? "Measurement",
  name: row.name ?? row.measurement_name ?? row.displayName ?? "Measurement",
  unit: row.unit ?? row.active_unit ?? row.unit_snapshot ?? row.defaultUnit ?? row.default_unit ?? "",
  defaultUnit: row.defaultUnit ?? row.default_unit ?? "",
  valueType: String(row.valueType ?? row.value_type ?? "NUMBER").toUpperCase(),
  valueOptions: parseValue(row.valueOptions ?? row.value_options) || [],
  enabled: asBool(row.enabledByDefault ?? row.enabled_by_default ?? row.companyDefaultEnabled ?? row.company_default_enabled),
  companyDefaultEnabled: asBool(row.companyDefaultEnabled ?? row.company_default_enabled),
  required: asBool(row.required ?? row.is_required),
  minimumValue: row.minimumValue ?? row.minimum_value ?? "",
  maximumValue: row.maximumValue ?? row.maximum_value ?? "",
  conversionFactor: row.conversionFactor ?? row.conversion_factor ?? "",
  conversionOffset: row.conversionOffset ?? row.conversion_offset ?? "",
});
const formatValue = (value) => value == null || value === "" ? "" : typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
const FREQUENT_KEYS = ["m149", "m051", "m008", "m009"];
const toCanonical = (measurement, value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const factor = Number(measurement.conversionFactor);
  const offset = Number(measurement.conversionOffset || 0);
  if (Number.isFinite(factor) && factor > 0) return parsed * factor + (Number.isFinite(offset) ? offset : 0);
  return measurement.unit === measurement.defaultUnit ? parsed : null;
};

/* ─────────────────────────────────────────────
   STYLES — premium blue design system
   (shared palette/type with the rest of the app suite)
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');

  .vc-root,.vc-root *{box-sizing:border-box}
  .vc-root{
    --paper:#F5F7FC; --card:#FFFFFF; --card-2:#F7F9FD;
    --ink:#101828; --ink-2:#344055; --muted:#66738A; --faint:#98A2B8;
    --line:#E4E9F5; --line-2:#D3DCF3;
    --blue:#3054E0; --blue-deep:#152C7A; --blue-tint:#EAEFFD;
    --emerald:#12946B; --emerald-tint:#E3F6ED;
    --amber:#AD7A0E; --amber-tint:#FBF1DD;
    --rose:#C1443E; --rose-tint:#FBEAEA;
    --font-body:'Inter',sans-serif; --font-disp:'Bricolage Grotesque',var(--font-body); --font-mono:'JetBrains Mono',monospace;
    --ease:cubic-bezier(.22,1,.36,1);
    display:flex;flex-direction:column;width:min(720px,calc(100vw - 32px));max-height:min(90vh,860px);overflow:hidden;
    background:var(--card);border:1px solid var(--line-2);border-radius:22px;box-shadow:0 30px 70px rgba(16,24,40,.22);
    color:var(--ink);font-family:var(--font-body);animation:vc-enter .25s var(--ease)
  }
  @keyframes vc-enter{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:none}}
  @keyframes vc-fade{from{opacity:0}to{opacity:1}}
  @keyframes vc-spin{to{transform:rotate(360deg)}}
  @keyframes vc-toast-in{from{opacity:0;transform:translateX(22px)}to{opacity:1;transform:translateX(0)}}
  .vc-spin{animation:vc-spin .8s linear infinite}

  .vc-head{display:flex;align-items:center;gap:13px;padding:20px 24px;background:var(--card-2);border-bottom:1px solid var(--line)}
  .vc-icon{width:42px;height:42px;display:flex;align-items:center;justify-content:center;border-radius:12px;background:var(--blue-tint);color:var(--blue);flex-shrink:0}
  .vc-head-info{flex:1;min-width:0}
  .vc-title{font-family:var(--font-disp);font-size:17px;font-weight:700;margin:0;letter-spacing:-.2px}
  .vc-subtitle{display:block;margin-top:3px;color:var(--muted);font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .vc-close{width:34px;height:34px;display:flex;align-items:center;justify-content:center;border:1px solid var(--line-2);
    border-radius:10px;background:var(--card);color:var(--muted);cursor:pointer;flex-shrink:0;transition:all .15s var(--ease)}
  .vc-close:hover{background:var(--rose-tint);color:var(--rose);border-color:#F1D1CF}

  .vc-main{flex:1;min-height:0;overflow:auto;padding:20px 24px;display:flex;flex-direction:column;gap:14px}
  .vc-alert{display:flex;align-items:center;gap:9px;padding:11px 14px;border:1px solid #F1D1CF;border-radius:11px;background:var(--rose-tint);color:#953530;font-size:12.5px;animation:vc-fade .2s ease}
  .vc-empty{padding:30px 16px;border:1.5px dashed var(--line-2);border-radius:14px;text-align:center;color:var(--muted);font-size:13px}

  .vc-toolbar{display:flex;align-items:center;justify-content:space-between;gap:10px}
  .vc-count{font-size:12px;color:var(--muted)}
  .vc-add{height:38px;display:inline-flex;align-items:center;gap:7px;padding:0 14px;border:1px solid var(--line-2);
    border-radius:10px;background:var(--blue-tint);color:var(--blue-deep);font:inherit;font-size:12.5px;font-weight:700;cursor:pointer;transition:all .15s var(--ease)}
  .vc-add:hover{background:#DEE7FC;transform:translateY(-1px)}
  .vc-add:active{transform:scale(.96)}

  .vc-list{display:flex;flex-direction:column;gap:10px}
  .vc-item{border:1px solid var(--line-2);border-radius:14px;background:var(--card);overflow:hidden;transition:border-color .2s,box-shadow .2s;animation:vc-fade .25s ease}
  .vc-item:hover{border-color:#C7D3F7;box-shadow:0 8px 20px rgba(48,84,224,.06)}
  .vc-item-head{display:flex;align-items:center;gap:11px;padding:13px 15px;border-bottom:1px solid var(--line);background:var(--card-2)}
  .vc-item-icon{width:30px;height:30px;border-radius:9px;background:var(--blue-tint);color:var(--blue);display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .vc-item-info{min-width:0;flex:1}
  .vc-item-info strong{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px;font-weight:600}
  .vc-item-info small{display:block;margin-top:2px;color:var(--muted);font-size:11px}
  .vc-default{padding:3px 8px;border:1px solid var(--line-2);border-radius:6px;color:var(--muted);font-size:9.5px;font-weight:600;white-space:nowrap;font-family:var(--font-mono)}
  .vc-remove{width:28px;height:28px;display:flex;align-items:center;justify-content:center;border:1px solid transparent;border-radius:8px;background:transparent;color:var(--faint);cursor:pointer;flex-shrink:0;transition:all .15s var(--ease)}
  .vc-remove:hover{background:var(--rose-tint);color:var(--rose)}

  .vc-input-area{padding:13px 15px;display:flex;flex-direction:column;gap:8px}
  .vc-label{font-size:11px;color:var(--muted);font-weight:600}
  .vc-input,.vc-select,.vc-textarea{width:100%;min-width:0;border:1px solid var(--line-2);border-radius:9px;background:var(--card);
    color:var(--ink);font:inherit;font-size:13px;padding:0 12px;outline:none;transition:border-color .15s,box-shadow .15s}
  .vc-input,.vc-select{height:40px}
  .vc-textarea{min-height:68px;padding:10px 12px;resize:vertical;line-height:1.5}
  .vc-input:focus,.vc-select:focus,.vc-textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(48,84,224,.12)}
  .vc-number-wrap{display:flex;align-items:center;gap:9px}
  .vc-number-wrap .vc-input{flex:1;font-family:var(--font-mono);font-variant-numeric:tabular-nums}
  .vc-unit{flex-shrink:0;color:var(--muted);font-size:11px;font-weight:700;font-family:var(--font-mono)}
  .vc-pair{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  .vc-check{display:inline-flex;align-items:center;gap:9px;font-size:13px;color:var(--ink-2);cursor:pointer}
  .vc-check input{width:17px;height:17px;accent-color:var(--blue);cursor:pointer}
  .vc-derived{display:flex;align-items:center;gap:9px;padding:11px 13px;border:1px solid #D2DCFB;border-radius:10px;background:var(--blue-tint);color:var(--blue-deep);font-size:12.5px;font-weight:600}
  .vc-prev{color:var(--faint);font-size:10.5px;font-family:var(--font-mono)}

  .vc-footer{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:16px 24px;border-top:1px solid var(--line-2);background:var(--card)}
  .vc-footnote{max-width:230px;color:var(--faint);font-size:10.5px;line-height:1.45}
  .vc-actions{display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end}
  .vc-btn{height:40px;padding:0 15px;display:inline-flex;align-items:center;justify-content:center;gap:7px;border:1px solid var(--line-2);
    border-radius:10px;background:var(--card);color:var(--ink-2);font:inherit;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s var(--ease)}
  .vc-btn:hover:not(:disabled){background:var(--card-2);transform:translateY(-1px)}
  .vc-btn:active:not(:disabled){transform:scale(.96)}
  .vc-btn:disabled{opacity:.55;cursor:not-allowed}
  .vc-primary{position:relative;overflow:hidden;border-color:transparent;background:linear-gradient(135deg,var(--blue),var(--blue-deep));color:#fff;box-shadow:0 10px 22px rgba(48,84,224,.28)}
  .vc-primary:hover:not(:disabled){box-shadow:0 14px 28px rgba(48,84,224,.36)}

  .vc-backdrop{position:fixed;inset:0;z-index:80;display:flex;align-items:center;justify-content:center;padding:18px;
    background:rgba(16,24,40,.45);backdrop-filter:blur(3px);animation:vc-fade .18s ease}
  .vc-picker{width:min(600px,96vw);max-height:min(82vh,740px);display:flex;flex-direction:column;overflow:hidden;
    background:var(--card);border:1px solid var(--line-2);border-radius:20px;box-shadow:0 26px 64px rgba(16,24,40,.26);animation:vc-enter .22s var(--ease)}
  .vc-picker-head{padding:18px 20px;border-bottom:1px solid var(--line)}
  .vc-picker-title{font-family:var(--font-disp);font-size:16px;font-weight:700;margin:0 0 12px}
  .vc-picker-tools{display:grid;grid-template-columns:minmax(0,1fr) 190px;gap:9px}
  .vc-search{height:40px;display:flex;align-items:center;gap:8px;padding:0 12px;border:1px solid var(--line-2);border-radius:10px;color:var(--muted);transition:border-color .15s}
  .vc-search:focus-within{border-color:var(--blue)}
  .vc-search input{width:100%;border:0;outline:0;color:var(--ink);font:inherit;font-size:12.5px;background:transparent}
  .vc-picker-list{padding:8px;overflow:auto;flex:1;min-height:0}
  .vc-pick-row{width:100%;min-height:54px;display:flex;align-items:center;gap:12px;padding:10px 12px;border:none;
    border-radius:11px;background:var(--card);text-align:left;color:var(--ink);cursor:pointer;transition:background .12s}
  .vc-pick-row:hover:not(:disabled){background:var(--blue-tint)}
  .vc-pick-row:disabled{opacity:.5;cursor:default}
  .vc-pick-copy{flex:1;min-width:0}
  .vc-pick-copy strong{display:block;font-size:12.5px;font-weight:600}
  .vc-pick-copy small{display:block;margin-top:2px;color:var(--faint);font-size:10.5px}
  .vc-picker-foot{padding:12px 16px;border-top:1px solid var(--line);display:flex;justify-content:space-between;align-items:center;gap:10px}
  .vc-custom{display:grid;grid-template-columns:1fr 1fr;gap:12px;padding:18px 20px}
  .vc-custom .vc-full{grid-column:1/-1}
  .vc-small{font-size:10.5px;color:var(--faint);line-height:1.45}
  .vc-confirm{width:min(380px,94vw);padding:24px;background:var(--card);border:1px solid var(--line-2);border-radius:20px;box-shadow:0 26px 64px rgba(16,24,40,.26);animation:vc-enter .2s var(--ease)}
  .vc-confirm h3{font-family:var(--font-disp);font-size:17px;margin:0 0 8px}
  .vc-confirm p{font-size:13px;color:var(--muted);line-height:1.6;margin:0 0 18px}

  .vc-toast-wrap{position:fixed;top:20px;right:20px;display:flex;flex-direction:column;gap:10px;z-index:100}
  .vc-toast{background:#fff;border-radius:13px;padding:13px 16px;box-shadow:0 16px 36px rgba(16,24,40,.16);border-left:3px solid var(--emerald);
    min-width:240px;max-width:310px;animation:vc-toast-in .25s ease;font-family:var(--font-body)}
  .vc-toast-title{font-size:12.5px;font-weight:700;color:var(--ink)}
  .vc-toast-sub{font-size:11.5px;color:var(--muted);margin-top:2px}

  @media(max-width:600px){
    .vc-root{width:100%;max-height:94dvh;border-radius:20px 20px 0 0}
    .vc-head{padding:16px 18px}
    .vc-main{padding:14px 16px}
    .vc-footer{padding:12px 16px;align-items:stretch;flex-direction:column}
    .vc-footnote{max-width:none}
    .vc-actions{display:grid;grid-template-columns:1fr 1fr}
    .vc-primary{grid-column:1/-1;order:-1}
    .vc-backdrop{align-items:flex-end;padding:0}
    .vc-picker{width:100%;max-height:90dvh;border-radius:20px 20px 0 0}
    .vc-picker-tools{grid-template-columns:1fr}
    .vc-custom{grid-template-columns:1fr}
    .vc-custom .vc-full{grid-column:auto}
    .vc-default{display:none}
  }
  @media(prefers-reduced-motion:reduce){.vc-root,.vc-item,.vc-alert,.vc-backdrop,.vc-picker,.vc-confirm,.vc-toast,.vc-spin{animation:none!important}}
`;

export default function DynamicVitalsCaptureModal({ onSave, onCancel, patientData, doctorId, clinicId }) {
  const appointmentId = patientData?.appointmentId;
  const resolvedDoctorId = doctorId ?? patientData?.doctorId;
  const [settings, setSettings] = useState([]);
  const [observations, setObservations] = useState([]);
  const [values, setValues] = useState({});
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [library, setLibrary] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Frequently used");
  const [customOpen, setCustomOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState({ name: "", category: "Custom", unit: "", valueType: "NUMBER", inputFormat: "", options: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingCustom, setAddingCustom] = useState(false);
  const [error, setError] = useState("");
  const [toasts, setToasts] = useState([]);

  const pushToast = (title, sub) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, title, sub }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  };

  useEffect(() => {
    let live = true;
    if (!resolvedDoctorId || !clinicId || !appointmentId) {
      setError("Doctor, clinic, or appointment context is missing.");
      setLoading(false);
      return () => { live = false; };
    }
    setLoading(true);
    setError("");
    Promise.all([
      vitalMeasurementService.getDoctorSettings(resolvedDoctorId, clinicId),
      vitalMeasurementService.getAppointmentMeasurements(appointmentId, resolvedDoctorId, clinicId),
    ]).then(([configured, saved]) => {
      if (!live) return;
      const normalized = (Array.isArray(configured) ? configured : []).map(normalize);
      const savedRows = Array.isArray(saved) ? saved : [];
      const latestById = new Map();
      savedRows.forEach((row) => latestById.set(Number(row.measurementId ?? row.measurement_id), row));
      const active = new Map(normalized.filter((row) => row.enabled).map((row) => [row.id, row]));
      savedRows.forEach((row) => {
        const id = Number(row.measurementId ?? row.measurement_id);
        const setting = normalized.find((item) => item.id === id);
        if (setting) active.set(id, setting);
      });
      const initial = {};
      active.forEach((row, id) => { initial[id] = latestById.has(id) ? parseValue(latestById.get(id).value) : ""; });
      setSettings(normalized);
      setObservations(savedRows);
      setValues(initial);
      setDirtyIds(new Set());
    }).catch((loadError) => setError(loadError?.response?.data?.message || "Unable to load measurements for this visit."))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [appointmentId, clinicId, resolvedDoctorId]);

  const observationById = useMemo(() => {
    const map = new Map();
    observations.forEach((row) => map.set(Number(row.measurementId ?? row.measurement_id), row));
    return map;
  }, [observations]);
  const activeMeasurements = useMemo(() => {
    const ids = new Set(Object.keys(values).map(Number));
    return settings.filter((row) => ids.has(row.id)).sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0) || a.displayName.localeCompare(b.displayName));
  }, [settings, values]);
  const categories = useMemo(() => ["Frequently used", "Doctor settings", "All categories", ...Array.from(new Set(library.map((row) => row.category).filter(Boolean))).sort()], [library]);
  const filteredLibrary = useMemo(() => {
    const source = category === "Doctor settings" ? settings.filter((row) => !row.enabled) : library;
    return source.filter((row) => {
      const q = search.trim().toLowerCase();
      if (category === "Frequently used" && !q) return false;
      const matchesCategory = category === "Frequently used" || category === "Doctor settings" || category === "All categories" || row.category === category;
      return matchesCategory && (!q || `${row.displayName} ${row.category} ${row.specialtyTags || ""}`.toLowerCase().includes(q));
    }).sort((a, b) => Number(b.companyDefaultEnabled) - Number(a.companyDefaultEnabled) || a.displayName.localeCompare(b.displayName));
  }, [category, library, search, settings]);
  const frequentMeasurements = useMemo(() => FREQUENT_KEYS.map((key) => library.find((row) => row.measurementKey === key)).filter(Boolean), [library]);

  const openPicker = async () => {
    setPickerOpen(true);
    setSearch("");
    setCategory("Frequently used");
    if (library.length) return;
    try {
      const rows = await vitalMeasurementService.getLibrary({ doctorId: resolvedDoctorId, clinicId });
      setLibrary((Array.isArray(rows) ? rows : []).map(normalize));
    } catch (loadError) { setError(loadError?.response?.data?.message || "Unable to load the measurement library."); }
  };
  const addMeasurement = (row) => {
    const selected = settings.find((item) => item.id === row.id) || row;
    setSettings((current) => current.some((item) => item.id === selected.id) ? current : [...current, selected]);
    setValues((current) => current[selected.id] === undefined ? { ...current, [selected.id]: "" } : current);
    setPickerOpen(false);
    pushToast("Measurement added", `${selected.displayName} is now part of this visit`);
  };
  const removeFromVisit = (id) => {
    const item = settings.find((row) => row.id === id);
    if (item?.enabled) return;
    setValues((current) => { const next = { ...current }; delete next[id]; return next; });
    setDirtyIds((current) => { const next = new Set(current); next.delete(id); return next; });
  };
  const updateValue = (row, value) => {
    setValues((current) => ({ ...current, [row.id]: value }));
    setDirtyIds((current) => new Set(current).add(row.id));
  };
  const getCanonical = (key) => {
    const row = activeMeasurements.find((item) => item.measurementKey === key);
    return row ? toCanonical(row, values[row.id]) : null;
  };
  const weightCanonical = getCanonical("m006");
  const heightCanonical = getCanonical("m007");
  const bmiRow = activeMeasurements.find((item) => item.measurementKey === "m008");
  const bmi = bmiRow && weightCanonical > 0 && heightCanonical > 0 ? (weightCanonical / ((heightCanonical / 100) ** 2)).toFixed(1) : null;

  const saveAndContinue = async () => {
    const missing = activeMeasurements.filter((row) => row.required && (values[row.id] == null || values[row.id] === ""));
    if (missing.length) {
      setError(`Required measurement missing: ${missing.map((row) => row.displayName).join(", ")}`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const pending = activeMeasurements.filter((row) => dirtyIds.has(row.id) && values[row.id] !== "");
      const payload = pending.map((row) => ({ measurementId: row.id, value: values[row.id], unit: row.unit || "" }));
      const heightRow = activeMeasurements.find((row) => row.measurementKey === "m007");
      const weightRow = activeMeasurements.find((row) => row.measurementKey === "m006");
      const bmiInputsChanged = (heightRow && dirtyIds.has(heightRow.id)) || (weightRow && dirtyIds.has(weightRow.id));
      if (bmiRow && bmi && bmiInputsChanged) payload.push({ measurementId: bmiRow.id, value: bmi, unit: bmiRow.unit || "kg/m²" });
      if (payload.length) await vitalMeasurementService.saveAppointmentMeasurements(appointmentId, resolvedDoctorId, clinicId, payload);
      await onSave?.(false);
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.message || "Unable to save visit measurements.");
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  const skip = async () => {
    setSaving(true);
    try { await onSave?.(true); }
    finally { setSaving(false); setSkipConfirm(false); }
  };
  const createCustom = async (event) => {
    event.preventDefault();
    if (!customDraft.name.trim()) return;
    setAddingCustom(true);
    setError("");
    try {
      const result = await vitalMeasurementService.createCustomMeasurement(resolvedDoctorId, clinicId, {
        name: customDraft.name.trim(), category: customDraft.category.trim() || "Custom",
        specialtyTags: "Other / Custom", unit: customDraft.unit.trim() || null,
        valueType: customDraft.valueType, inputFormat: customDraft.inputFormat.trim() || null,
        options: customDraft.valueType === "SELECT" ? customDraft.options.split(",").map((item) => item.trim()).filter(Boolean) : undefined,
      });
      const row = normalize(result);
      setSettings((current) => current.some((item) => item.id === row.id) ? current : [...current, row]);
      setValues((current) => ({ ...current, [row.id]: "" }));
      setLibrary((current) => [...current, row]);
      setPickerOpen(false);
      setCustomOpen(false);
      setCustomDraft({ name: "", category: "Custom", unit: "", valueType: "NUMBER", inputFormat: "", options: "" });
      pushToast("Custom measurement created", `${row.displayName} added to this visit and saved to your library`);
    } catch (createError) { setError(createError?.response?.data?.message || "Unable to create this custom measurement."); }
    finally { setAddingCustom(false); }
  };

  const valueEditor = (row) => {
    const value = values[row.id] ?? "";
    if (row.measurementKey === "m008") return <div className="vc-derived">{bmi ? `Calculated BMI: ${bmi} ${row.unit || "kg/m²"}` : "Add weight and height with convertible units to calculate BMI."}</div>;
    if (row.measurementKey === "m001" || (row.valueType === "COMPOUND" && /blood pressure/i.test(row.displayName))) {
      const compound = value && typeof value === "object" ? value : {};
      return <div className="vc-pair">{[["systolic", "Systolic"], ["diastolic", "Diastolic"]].map(([key, label]) => <label className="vc-label" key={key}>{label}<input className="vc-input" style={{ marginTop: 6 }} type="number" value={compound[key] ?? ""} onChange={(event) => updateValue(row, { ...compound, [key]: event.target.value })} placeholder={label} /></label>)}</div>;
    }
    if (row.valueType === "NUMBER") return <div className="vc-number-wrap"><input className="vc-input" type="number" min={row.minimumValue || undefined} max={row.maximumValue || undefined} value={value} onChange={(event) => updateValue(row, event.target.value)} placeholder="Enter value" /><span className="vc-unit">{row.unit}</span></div>;
    if (row.valueType === "DATE") return <input className="vc-input" type="date" value={value} onChange={(event) => updateValue(row, event.target.value)} />;
    if (row.valueType === "BOOLEAN") return <label className="vc-check"><input type="checkbox" checked={value === true} onChange={(event) => updateValue(row, event.target.checked)} /> Yes</label>;
    if (row.valueType === "SELECT") return <select className="vc-select" value={value} onChange={(event) => updateValue(row, event.target.value)}><option value="">Select…</option>{(Array.isArray(row.valueOptions) ? row.valueOptions : []).map((option) => <option key={option}>{option}</option>)}</select>;
    if (row.valueType === "TEXT") return <textarea className="vc-textarea" value={typeof value === "string" ? value : formatValue(value)} onChange={(event) => updateValue(row, event.target.value)} placeholder="Enter observation" />;
    return <textarea className="vc-textarea" value={formatValue(value)} onChange={(event) => {
      const raw = event.target.value;
      try { updateValue(row, raw ? JSON.parse(raw) : ""); }
      catch { updateValue(row, raw); }
    }} placeholder={row.inputFormat || "Enter a JSON object or array"} />;
  };

  return <div className="vc-root" role="dialog" aria-modal="true" aria-labelledby="vc-title">
    <style>{CSS}</style>
    <header className="vc-head">
      <span className="vc-icon"><Activity size={20} /></span>
      <span className="vc-head-info">
        <h2 className="vc-title" id="vc-title">Vitals &amp; measurements</h2>
        <span className="vc-subtitle">{patientData?.patientName || "Patient"} · This visit</span>
      </span>
      <button className="vc-close" type="button" onClick={onCancel} aria-label="Close"><X size={16} /></button>
    </header>

    <main className="vc-main">
      {error && <div className="vc-alert" role="alert"><AlertCircle size={15} /> {error}</div>}
      {loading ? <div className="vc-empty"><Loader2 size={15} className="vc-spin" style={{ verticalAlign: "-3px", marginRight: 6 }} /> Loading doctor settings and this visit…</div> : <>
        <div className="vc-toolbar">
          <span className="vc-count">{activeMeasurements.length} measurement{activeMeasurements.length === 1 ? "" : "s"} in this visit</span>
          <button type="button" className="vc-add" onClick={openPicker}><Plus size={14} /> Add measurement</button>
        </div>
        {activeMeasurements.length ? <div className="vc-list">{activeMeasurements.map((row) => {
          const previous = observationById.get(row.id);
          return <article className="vc-item" key={row.id}>
            <div className="vc-item-head">
              <span className="vc-item-icon"><Activity size={15} /></span>
              <span className="vc-item-info"><strong>{row.displayName}{row.required ? " *" : ""}</strong><small>{row.category} · {row.unit || "No unit"}{previous ? " · Recorded this visit" : ""}</small></span>
              {row.companyDefaultEnabled && <span className="vc-default">Default</span>}
              {!row.enabled && <button className="vc-remove" type="button" onClick={() => removeFromVisit(row.id)} aria-label={`Remove ${row.displayName} from this visit`}><X size={14} /></button>}
            </div>
            <div className="vc-input-area">
              {valueEditor(row)}
              {previous && <small className="vc-prev">Previous value: {formatValue(parseValue(previous.value))} {previous.unit || ""}</small>}
            </div>
          </article>;
        })}</div> : <div className="vc-empty">No measurements are enabled for this doctor. Add one for this visit or skip entry.</div>}
      </>}
    </main>

    <footer className="vc-footer">
      <span className="vc-footnote">Library selections apply to this visit. Custom measurements are saved to your library, not enabled by default.</span>
      <span className="vc-actions">
        <button type="button" className="vc-btn" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="button" className="vc-btn" onClick={() => setCustomOpen(true)} disabled={saving || loading}><Plus size={13} /> Custom</button>
        <button type="button" className="vc-btn" onClick={() => setSkipConfirm(true)} disabled={saving || loading}>Skip</button>
        <button type="button" className="vc-btn vc-primary" onClick={saveAndContinue} disabled={saving || loading}>
          {saving ? <Loader2 size={13} className="vc-spin" /> : <Check size={13} />}
          {saving ? "Saving…" : "Save & mark arrival"}
        </button>
      </span>
    </footer>

    {pickerOpen && <div className="vc-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setPickerOpen(false); }}>
      <section className="vc-picker" role="dialog" aria-modal="true" aria-labelledby="vc-picker-title">
        <div className="vc-picker-head">
          <h3 className="vc-picker-title" id="vc-picker-title">Add a measurement to this visit</h3>
          <div className="vc-picker-tools">
            <label className="vc-search"><Search size={14} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search measurements" /></label>
            <select className="vc-select" value={category} onChange={(event) => setCategory(event.target.value)}>{categories.map((item) => <option key={item}>{item}</option>)}</select>
          </div>
        </div>
        <div className="vc-picker-list">
          {!search && category === "Frequently used" && <>
            <div className="vc-count" style={{ padding: "7px 10px", fontWeight: 700 }}>Frequently used</div>
            {frequentMeasurements.map((row) => { const exists = activeMeasurements.some((item) => item.id === row.id); return <button className="vc-pick-row" type="button" key={`freq-${row.id}`} disabled={exists} onClick={() => addMeasurement(row)}><span className="vc-item-icon"><Activity size={14} /></span><span className="vc-pick-copy"><strong>{row.displayName}</strong><small>{row.category} · {row.unit || "No unit"}</small></span>{exists ? <span className="vc-count">Added</span> : <Plus size={14} />}</button>; })}
          </>}
          {filteredLibrary.map((row) => { const exists = activeMeasurements.some((item) => item.id === row.id); return <button className="vc-pick-row" type="button" key={row.id} disabled={exists} onClick={() => addMeasurement(row)}><span className="vc-item-icon"><Activity size={14} /></span><span className="vc-pick-copy"><strong>{row.displayName}</strong><small>{row.category} · {row.unit || "No unit"}{row.companyDefaultEnabled ? " · Company default" : ""}</small></span>{exists ? <span className="vc-count">Added</span> : <Plus size={14} />}</button>; })}
          {(category !== "Frequently used" || search) && !filteredLibrary.length && <div className="vc-empty">No matching measurements.</div>}
        </div>
        <div className="vc-picker-foot"><span className="vc-small">One-off selection does not change defaults.</span><button className="vc-btn" type="button" onClick={() => setCustomOpen(true)}><Plus size={13} /> Create custom</button></div>
      </section>
    </div>}

    {customOpen && <div className="vc-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setCustomOpen(false); }}>
      <section className="vc-picker" role="dialog" aria-modal="true" aria-labelledby="vc-custom-title">
        <div className="vc-picker-head">
          <h3 className="vc-picker-title" id="vc-custom-title">Create custom measurement</h3>
          <span className="vc-small">Available in the library after this visit; not enabled by default.</span>
        </div>
        <form className="vc-custom" onSubmit={createCustom}>
          <label className="vc-label vc-full">Name<input className="vc-input" style={{ marginTop: 6 }} required maxLength={120} value={customDraft.name} onChange={(event) => setCustomDraft((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. Wound length" /></label>
          <label className="vc-label">Category<input className="vc-input" style={{ marginTop: 6 }} value={customDraft.category} onChange={(event) => setCustomDraft((current) => ({ ...current, category: event.target.value }))} /></label>
          <label className="vc-label">Unit<input className="vc-input" style={{ marginTop: 6 }} maxLength={80} value={customDraft.unit} onChange={(event) => setCustomDraft((current) => ({ ...current, unit: event.target.value }))} placeholder="Custom or blank" /></label>
          <label className="vc-label">Value type<select className="vc-select" style={{ marginTop: 6 }} value={customDraft.valueType} onChange={(event) => setCustomDraft((current) => ({ ...current, valueType: event.target.value }))}>{["NUMBER", "TEXT", "BOOLEAN", "SELECT", "STRUCTURED", "DATE", "COMPOUND"].map((type) => <option key={type}>{type}</option>)}</select></label>
          <label className="vc-label">Format hint<input className="vc-input" style={{ marginTop: 6 }} value={customDraft.inputFormat} onChange={(event) => setCustomDraft((current) => ({ ...current, inputFormat: event.target.value }))} /></label>
          {customDraft.valueType === "SELECT" && <label className="vc-label vc-full">Options, comma separated<input className="vc-input" style={{ marginTop: 6 }} required value={customDraft.options} onChange={(event) => setCustomDraft((current) => ({ ...current, options: event.target.value }))} placeholder="Mild, Moderate, Severe" /></label>}
          <div className="vc-picker-foot vc-full" style={{ padding: 0, border: "none", marginTop: 4 }}>
            <button className="vc-btn" type="button" onClick={() => setCustomOpen(false)} disabled={addingCustom}>Cancel</button>
            <button className="vc-btn vc-primary" type="submit" disabled={addingCustom || !customDraft.name.trim()}>
              {addingCustom ? <Loader2 size={13} className="vc-spin" /> : <Plus size={13} />}
              {addingCustom ? "Creating…" : "Create & add to visit"}
            </button>
          </div>
        </form>
      </section>
    </div>}

    {skipConfirm && <div className="vc-backdrop" role="presentation">
      <section className="vc-confirm" role="alertdialog" aria-modal="true" aria-labelledby="vc-skip-title">
        <h3 id="vc-skip-title">Skip measurements?</h3>
        <p>The visit will continue without recording measurements. This won't change the doctor's defaults.</p>
        <div className="vc-actions">
          <button className="vc-btn" type="button" onClick={() => setSkipConfirm(false)} disabled={saving}>Cancel</button>
          <button className="vc-btn vc-primary" type="button" onClick={skip} disabled={saving}>{saving ? <Loader2 size={13} className="vc-spin" /> : null}Skip and continue</button>
        </div>
      </section>
    </div>}

    <div className="vc-toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className="vc-toast">
          <div className="vc-toast-title">{t.title}</div>
          {t.sub && <div className="vc-toast-sub">{t.sub}</div>}
        </div>
      ))}
    </div>
  </div>;
}